import is from '@sindresorhus/is';
import { first } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { StoreApi } from 'zustand';

import { useTranslate } from '~/shared/context/use-translate';
import type {
    Competencies,
    JobProfileComponent,
    JobProfileLegacy,
    SectionTagDefinition,
} from '~/shared/data-contract/job-profile';
import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import type { JobProfileSkill } from '~/shared/data-contract/skill';

import { useCompetencies } from '../api/use-competencies';
import type { JobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { useUpdateJobProfile } from '../api/use-update-job-profile';
import { useTrackSkillChanges } from '../hooks/analytics/use-track-skill-changes';
import { useTrackVersionSaved } from '../hooks/analytics/use-track-version-saved';
import { useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { normalizeJobProfile } from '../utils/normalize-job-profile';
import { stripCommentMarks } from './comments/strip-comment-marks';
import type { DraftVersion, DraftVersionStore } from './draft-version-store';
import { useScrollToInvalidSkill } from './skills/use-scroll-to-invalid-skill';
import { addSkillsToCompetencies, WITHOUT_COMPETENCY_ID } from './skills/use-skills-with-compentencies';
import type { ProfileForm } from './types';

type UseJobProfileFormActionsParams = {
    readonly architectureId: string;
    readonly draftStore: StoreApi<DraftVersionStore>;
    readonly formMethods: UseFormReturn<ProfileForm>;
    readonly hasDraft: boolean;
    readonly item: JobItemDetails;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly parentRoleId?: string;
    readonly profile: JobProfileComponent;
    readonly regeneratedSectionTags?: readonly SectionTagDefinition[];
    readonly resetSkillActionStore: () => void;
    readonly selectedVersion: string | undefined;
    readonly setActiveVersion: (versionId: string) => void;
    readonly setIsEditing: (editing: boolean) => void;
};

export type SaveJobProfileOptions = { readonly withAutoTranslation: boolean };

const normalizeSkillCompetencyId = (skill: JobProfileSkill): JobProfileSkill => ({
    ...skill,
    competencyId:
        skill.skillType === 'language' || skill.competencyId === WITHOUT_COMPETENCY_ID ? undefined : skill.competencyId,
});

type UseJobProfileFormActionsResult = {
    readonly allCompetencies: Competencies | undefined;
    readonly formSkills: readonly JobProfileSkill[];
    readonly handleNewVersion: () => void;
    readonly handleSaveSubmit: (data: ProfileForm, options: SaveJobProfileOptions) => Promise<void>;
    readonly handleSaveValidationError: () => void;
    readonly versions: JobProfileVersions | undefined;
};

const stripStructuredCommentMarks = (
    structured: JobProfileComponent['structured'],
): JobProfileComponent['structured'] =>
    structured?.map((structuredItem) => ({
        ...structuredItem,
        value: stripCommentMarks(structuredItem.value),
    }));

const stripTranslationCommentMarks = (
    translations: JobProfileLegacy['translations'],
): JobProfileLegacy['translations'] =>
    translations.map((translation) => ({
        ...translation,
        structured: stripStructuredCommentMarks(translation.structured),
    }));

export const useJobProfileFormActions = ({
    architectureId,
    draftStore,
    formMethods,
    hasDraft,
    item,
    itemId,
    itemType,
    parentRoleId,
    profile,
    regeneratedSectionTags,
    resetSkillActionStore,
    selectedVersion,
    setActiveVersion,
    setIsEditing,
}: UseJobProfileFormActionsParams): UseJobProfileFormActionsResult => {
    const { activeLanguage, activeLanguageIndex } = useActiveLanguage();
    const translate = useTranslate();

    const { mutateAsync: updateJobProfile } = useUpdateJobProfile();
    const { data: versions, refetch: refetchVersions } = useJobItemVersions({ architectureId, itemId, itemType });
    const { data: allCompetencies } = useCompetencies();
    const { enqueueSnackbar } = useSnackbar();
    const formSkills = useWatch({ control: formMethods.control, name: 'skills' });
    const { captureInitialSkills, trackSkillChanges } = useTrackSkillChanges({ architectureId });
    const { setIsNewVersionSave, trackVersionSaved } = useTrackVersionSaved({ architectureId });
    const scrollToInvalidSkill = useScrollToInvalidSkill(formMethods);

    const handleNewVersion = (): void => {
        setIsNewVersionSave(true);
        setIsEditing(true);

        if (versions === undefined) {
            return;
        }

        if (selectedVersion === undefined) {
            return;
        }

        const nextVersion = (first(versions)?.version ?? 0) + 1;
        const draftVersion: DraftVersion = {
            ...item,
            versionInfo: {
                createdAt: new Date().toISOString(),
                isPublished: false,
                isReviewed: false,
                version: nextVersion,
            },
        };

        draftStore.getState().addDraft(draftVersion);

        captureInitialSkills(profile.skills ?? []);
        formMethods.reset({
            competencies: addSkillsToCompetencies([...(profile.competencies?.values() ?? [])], profile.skills ?? []),
            skills: profile.skills ?? [],
            translations: profile.translations,
            versionNote: undefined,
        });
    };

    const handleSaveSubmit = useCallback(
        async (
            { competencies, translations, versionNote }: ProfileForm,
            { withAutoTranslation }: SaveJobProfileOptions,
        ): Promise<void> => {
            const flatSkills = competencies.flatMap((competency) => competency.skills);
            const normalizedSkills = flatSkills.map(normalizeSkillCompetencyId);
            const normalizedCompetencies = addSkillsToCompetencies(competencies, normalizedSkills);
            const trimmedNote = versionNote?.trim();
            const versionComment = is.nonEmptyString(trimmedNote) ? trimmedNote : undefined;
            const sanitizedTranslations = stripTranslationCommentMarks(translations);
            const sanitizedProfile = {
                ...profile,
                structured: stripStructuredCommentMarks(profile.structured),
                translations: stripTranslationCommentMarks(profile.translations),
            };

            try {
                await updateJobProfile({
                    acceptLanguage: activeLanguage,
                    architectureId,
                    jobItemType: {
                        id: itemId,
                        type: itemType,
                    },
                    jobProfile: normalizeJobProfile(
                        {
                            ...sanitizedProfile,
                            sectionTags: is.undefined(regeneratedSectionTags)
                                ? profile.sectionTags
                                : [...regeneratedSectionTags],
                            translations: sanitizedTranslations.filter((_, index) => index === activeLanguageIndex),
                        },
                        activeLanguage,
                        normalizedSkills,
                    ),
                    version: selectedVersion,
                    versionComment,
                    withAutoTranslation,
                });
                const updatedVersions = await refetchVersions();
                const latestVersion = first(updatedVersions.data);

                if (updatedVersions.data !== undefined) {
                    setActiveVersion(latestVersion?.version.toString() ?? '');
                }

                trackVersionSaved({
                    item,
                    itemId,
                    itemType,
                    latestVersion,
                    parentRoleId,
                });

                trackSkillChanges(normalizedSkills, {
                    itemId,
                    itemType,
                    parentRoleId,
                    versionId: latestVersion?.itemId ?? itemId,
                });

                if (hasDraft) {
                    draftStore.getState().removeDraft(itemId);
                }

                formMethods.reset({
                    competencies: normalizedCompetencies,
                    skills: normalizedSkills,
                    translations: sanitizedTranslations,
                    versionNote: undefined,
                });

                setIsEditing(false);
                resetSkillActionStore();
            } catch (error: unknown) {
                enqueueSnackbar((error as { message: string }).message, { variant: 'error' });
            }
        },
        [
            activeLanguage,
            activeLanguageIndex,
            architectureId,
            draftStore,
            enqueueSnackbar,
            formMethods,
            hasDraft,
            item,
            itemId,
            itemType,
            parentRoleId,
            profile,
            refetchVersions,
            regeneratedSectionTags,
            resetSkillActionStore,
            selectedVersion,
            setActiveVersion,
            setIsEditing,
            trackSkillChanges,
            trackVersionSaved,
            updateJobProfile,
        ],
    );

    const handleSaveValidationError = useCallback(() => {
        enqueueSnackbar(translate('pages.job-architecture-v2.validation-error-skills-missing-proficiency'), {
            variant: 'error',
        });

        scrollToInvalidSkill();
    }, [enqueueSnackbar, scrollToInvalidSkill, translate]);

    return {
        allCompetencies,
        formSkills,
        handleNewVersion,
        handleSaveSubmit,
        handleSaveValidationError,
        versions,
    };
};
